import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { CNetRow, CNetActions, CNetResult, CNetHintBar, G } from './_cnet';

// AWG resistance (Ω/km) for copper - rough tabulated
const AWG_R_COPPER = {
  '14 AWG': 8.286, '12 AWG': 5.211, '10 AWG': 3.277, '8 AWG': 2.061,
  '6 AWG': 1.296, '4 AWG': 0.8152, '3 AWG': 0.6465, '2 AWG': 0.5127,
  '1 AWG': 0.4066, '1/0 AWG': 0.3224, '2/0 AWG': 0.2557, '3/0 AWG': 0.2028,
  '4/0 AWG': 0.1608, '250 kcmil': 0.1360, '350 kcmil': 0.09720,
  '500 kcmil': 0.06808, '750 kcmil': 0.04539, '1000 kcmil': 0.03404,
};
const AWG_LIST = Object.keys(AWG_R_COPPER);
// Aluminum ~ 1.6× copper
const NEC_XL = 0.05; // Ω/km rough reactance for magnetic conduit
const PHASE_FACTOR = { 'DC': 2, 'AC single phase': 2, 'AC three phase': Math.sqrt(3) };

function TabButton({ active, onClick, label, testId }) {
  return <button data-testid={testId} className={`cnet-tab ${active ? 'active' : ''}`} onClick={onClick}>{label}</button>;
}

export default function VoltageDropCalc() {
  const [tab, setTab] = useState('nec');
  return (
    <CalcShell look="cnet" showActions={false} title="Voltage Drop Calculator" subtitle="Estimate the voltage drop of an electrical circuit using NEC data, estimated resistance, or custom impedance.">
      <p className="text-sm text-slate-700 mb-3">This is a calculator for the estimation of the voltage drop of an electrical circuit. The "NEC data" tab calculates based on the resistance and reactance data from the National Electrical Code (NEC). The "Estimated resistance" tab calculates based on the resistance data estimated from the wire size. Click the "Other" tab to use customized resistance or impedance data, such as data from other standards or wire manufacturers.</p>
      <CNetHintBar />
      <div className="flex gap-0">
        <TabButton active={tab === 'nec'} onClick={() => setTab('nec')} label="NEC data" testId="vd-tab-nec" />
        <TabButton active={tab === 'est'} onClick={() => setTab('est')} label="Estimated resistance" testId="vd-tab-est" />
        <TabButton active={tab === 'oth'} onClick={() => setTab('oth')} label="Other" testId="vd-tab-oth" />
      </div>
      <div className="cnet-tab-body">
        {tab === 'nec' && <NECTab />}
        {tab === 'est' && <EstTab />}
        {tab === 'oth' && <OtherTab />}
      </div>
    </CalcShell>
  );
}

function NECTab() {
  const [material, setMaterial] = useState('Copper');
  const [wire, setWire] = useState('3 AWG');
  const [conduit, setConduit] = useState('Steel');
  const [pf, setPf] = useState(0.85);
  const [voltage, setVoltage] = useState(120);
  const [phase, setPhase] = useState('AC single phase');
  const [nCond, setNCond] = useState('single set of conductors');
  const [dist, setDist] = useState(500);
  const [distU, setDistU] = useState('meters');
  const [load, setLoad] = useState(1);
  const [r, setR] = useState(null);
  const calc = () => {
    const rOhmKm = AWG_R_COPPER[wire] * (material === 'Aluminum' ? 1.6 : 1);
    const xOhmKm = conduit === 'PVC' ? 0.04 : NEC_XL;
    const distKm = (Number(dist) * (distU === 'meters' ? 1 : distU === 'feet' ? 0.3048 : 1)) / 1000;
    const setsFactor = nCond.includes('two') ? 0.5 : nCond.includes('three') ? 1 / 3 : 1;
    const R = rOhmKm * distKm * setsFactor;
    const X = xOhmKm * distKm * setsFactor;
    const Z = Math.sqrt(R * R + X * X);
    const drop = PHASE_FACTOR[phase] * Number(load) * (R * Number(pf) + X * Math.sin(Math.acos(Number(pf))));
    setR({ drop, endV: Number(voltage) - drop, pct: (drop / Number(voltage)) * 100, R, X, Z });
  };
  return (
    <>
      <div className="cnet-row-2col"><div className="cnet-label">Wire material</div>
        <select className="cnet-select" value={material} onChange={e => setMaterial(e.target.value)}><option>Copper</option><option>Aluminum</option></select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Wire size</div>
        <select className="cnet-select" value={wire} onChange={e => setWire(e.target.value)}>{AWG_LIST.map(a => <option key={a}>{a}</option>)}</select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Material of conduit</div>
        <select className="cnet-select" value={conduit} onChange={e => setConduit(e.target.value)}><option>Steel</option><option>Aluminum</option><option>PVC</option></select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Power factor (PF)</div><input className="cnet-input" type="number" step="0.01" value={pf} onChange={e => setPf(e.target.value)} /></div>
      <div className="cnet-row-2col"><div className="cnet-label">Voltage</div><input className="cnet-input" type="number" value={voltage} onChange={e => setVoltage(e.target.value)} /></div>
      <div className="cnet-row-2col"><div className="cnet-label">Phase</div>
        <select className="cnet-select" value={phase} onChange={e => setPhase(e.target.value)}>{Object.keys(PHASE_FACTOR).map(k => <option key={k}>{k}</option>)}</select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Number of conductors</div>
        <select className="cnet-select" value={nCond} onChange={e => setNCond(e.target.value)}><option>single set of conductors</option><option>two sets of conductors</option><option>three sets of conductors</option></select>
      </div>
      <CNetRow label="Distance (one-way)">
        <input className="cnet-input" type="number" value={dist} onChange={e => setDist(e.target.value)} />
        <select className="cnet-select" value={distU} onChange={e => setDistU(e.target.value)}><option>meters</option><option>feet</option></select>
      </CNetRow>
      <div className="cnet-row-2col"><div className="cnet-label">Load current</div>
        <div className="flex gap-2 items-center"><input className="cnet-input" type="number" value={load} onChange={e => setLoad(e.target.value)} /><span>Amps</span></div>
      </div>
      <CNetActions onCalc={calc} onClear={() => setR(null)} testId="vd-nec" />
      {r && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="vd-nec-result">
            <tbody>
              <tr><td>Voltage drop:</td><td><b className="text-[#4c7b25]">{G(r.drop, 3)}</b> V ({G(r.pct, 2)}%)</td></tr>
              <tr><td>Voltage at load:</td><td>{G(r.endV, 3)} V</td></tr>
              <tr><td>Total line resistance R:</td><td>{G(r.R, 4)} Ω</td></tr>
              <tr><td>Total line reactance X:</td><td>{G(r.X, 4)} Ω</td></tr>
              <tr><td>Total line impedance Z:</td><td>{G(r.Z, 4)} Ω</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}

function EstTab() {
  const [material, setMaterial] = useState('Copper');
  const [wire, setWire] = useState('1 AWG (83.7 kcmil)');
  const [voltage, setVoltage] = useState(120);
  const [phase, setPhase] = useState('AC single phase');
  const [nCond, setNCond] = useState('single set of conductors');
  const [dist, setDist] = useState(500);
  const [distU, setDistU] = useState('meters');
  const [load, setLoad] = useState(1);
  const [r, setR] = useState(null);
  const OPTIONS = ['14 AWG (4.11 kcmil)', '12 AWG (6.53 kcmil)', '10 AWG (10.4 kcmil)', '8 AWG (16.5 kcmil)', '6 AWG (26.3 kcmil)', '4 AWG (41.7 kcmil)', '2 AWG (66.4 kcmil)', '1 AWG (83.7 kcmil)', '1/0 AWG (105.6 kcmil)', '2/0 AWG (133.1 kcmil)', '4/0 AWG (211.6 kcmil)', '250 kcmil', '500 kcmil'];
  const calc = () => {
    // approximate resistance in Ω/km derived from kcmil (10.371 Ω-cmil/ft ÷ kcmil × 1000 ft/mi)
    const kcmil = parseFloat(wire.match(/([\d.]+)\s*kcmil/)?.[1] || 100);
    const rOhmKm = (10.371 / kcmil) * 3.28084;
    const rMat = rOhmKm * (material === 'Aluminum' ? 1.6 : 1);
    const distKm = (Number(dist) * (distU === 'meters' ? 1 : distU === 'feet' ? 0.3048 : 1)) / 1000;
    const setsFactor = nCond.includes('two') ? 0.5 : nCond.includes('three') ? 1 / 3 : 1;
    const R = rMat * distKm * setsFactor;
    const drop = PHASE_FACTOR[phase] * Number(load) * R;
    setR({ drop, endV: Number(voltage) - drop, pct: (drop / Number(voltage)) * 100, R });
  };
  return (
    <>
      <div className="cnet-row-2col"><div className="cnet-label">Wire material</div>
        <select className="cnet-select" value={material} onChange={e => setMaterial(e.target.value)}><option>Copper</option><option>Aluminum</option></select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Wire size</div>
        <select className="cnet-select" value={wire} onChange={e => setWire(e.target.value)}>{OPTIONS.map(o => <option key={o}>{o}</option>)}</select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Voltage</div><input className="cnet-input" type="number" value={voltage} onChange={e => setVoltage(e.target.value)} /></div>
      <div className="cnet-row-2col"><div className="cnet-label">Phase</div>
        <select className="cnet-select" value={phase} onChange={e => setPhase(e.target.value)}>{Object.keys(PHASE_FACTOR).map(k => <option key={k}>{k}</option>)}</select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Number of conductors</div>
        <select className="cnet-select" value={nCond} onChange={e => setNCond(e.target.value)}><option>single set of conductors</option><option>two sets of conductors</option><option>three sets of conductors</option></select>
      </div>
      <CNetRow label="Distance (one-way)">
        <input className="cnet-input" type="number" value={dist} onChange={e => setDist(e.target.value)} />
        <select className="cnet-select" value={distU} onChange={e => setDistU(e.target.value)}><option>meters</option><option>feet</option></select>
      </CNetRow>
      <div className="cnet-row-2col"><div className="cnet-label">Load current</div>
        <div className="flex gap-2 items-center"><input className="cnet-input" type="number" value={load} onChange={e => setLoad(e.target.value)} /><span>Amps</span></div>
      </div>
      <CNetActions onCalc={calc} onClear={() => setR(null)} testId="vd-est" />
      {r && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="vd-est-result">
            <tbody>
              <tr><td>Voltage drop:</td><td><b className="text-[#4c7b25]">{G(r.drop, 3)}</b> V ({G(r.pct, 2)}%)</td></tr>
              <tr><td>Voltage at load:</td><td>{G(r.endV, 3)} V</td></tr>
              <tr><td>Total line resistance:</td><td>{G(r.R, 4)} Ω</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}

function OtherTab() {
  const [imp, setImp] = useState(1.2);
  const [impU, setImpU] = useState('Ω/km');
  const [voltage, setVoltage] = useState(120);
  const [phase, setPhase] = useState('AC single phase');
  const [nCond, setNCond] = useState('single set of conductors');
  const [dist, setDist] = useState(500);
  const [distU, setDistU] = useState('meters');
  const [load, setLoad] = useState(1);
  const [r, setR] = useState(null);
  const calc = () => {
    let Zperkm = Number(imp);
    if (impU === 'Ω/1000 ft') Zperkm = Zperkm / 0.3048;
    if (impU === 'Ω/mi') Zperkm = Zperkm / 1.609;
    const distKm = (Number(dist) * (distU === 'meters' ? 1 : 0.3048)) / 1000;
    const setsFactor = nCond.includes('two') ? 0.5 : nCond.includes('three') ? 1 / 3 : 1;
    const Z = Zperkm * distKm * setsFactor;
    const drop = PHASE_FACTOR[phase] * Number(load) * Z;
    setR({ drop, endV: Number(voltage) - drop, pct: (drop / Number(voltage)) * 100, Z });
  };
  return (
    <>
      <CNetRow label="Wire impedence or resistance">
        <input className="cnet-input" type="number" value={imp} onChange={e => setImp(e.target.value)} />
        <select className="cnet-select" value={impU} onChange={e => setImpU(e.target.value)}><option>Ω/km</option><option>Ω/1000 ft</option><option>Ω/mi</option></select>
      </CNetRow>
      <div className="cnet-row-2col"><div className="cnet-label">Voltage</div><input className="cnet-input" type="number" value={voltage} onChange={e => setVoltage(e.target.value)} /></div>
      <div className="cnet-row-2col"><div className="cnet-label">Phase</div>
        <select className="cnet-select" value={phase} onChange={e => setPhase(e.target.value)}>{Object.keys(PHASE_FACTOR).map(k => <option key={k}>{k}</option>)}</select>
      </div>
      <div className="cnet-row-2col"><div className="cnet-label">Number of conductors</div>
        <select className="cnet-select" value={nCond} onChange={e => setNCond(e.target.value)}><option>single set of conductors</option><option>two sets of conductors</option><option>three sets of conductors</option></select>
      </div>
      <CNetRow label="Distance (one-way)">
        <input className="cnet-input" type="number" value={dist} onChange={e => setDist(e.target.value)} />
        <select className="cnet-select" value={distU} onChange={e => setDistU(e.target.value)}><option>meters</option><option>feet</option></select>
      </CNetRow>
      <div className="cnet-row-2col"><div className="cnet-label">Load current</div>
        <div className="flex gap-2 items-center"><input className="cnet-input" type="number" value={load} onChange={e => setLoad(e.target.value)} /><span>Amps</span></div>
      </div>
      <CNetActions onCalc={calc} onClear={() => setR(null)} testId="vd-oth" />
      {r && (
        <CNetResult>
          <table className="cnet-info-table" data-testid="vd-oth-result">
            <tbody>
              <tr><td>Voltage drop:</td><td><b className="text-[#4c7b25]">{G(r.drop, 3)}</b> V ({G(r.pct, 2)}%)</td></tr>
              <tr><td>Voltage at load:</td><td>{G(r.endV, 3)} V</td></tr>
              <tr><td>Total line impedance:</td><td>{G(r.Z, 4)} Ω</td></tr>
            </tbody>
          </table>
        </CNetResult>
      )}
    </>
  );
}
